import { sum } from "@/lib/sum";
import type {
	DirectoryTreeEntry,
	FileTreeEntry,
	TreeEntry,
} from "@/components/file-tree/useFileTree";
import {
	createEnvironmentFile,
	createEnvironmentFolder,
	deleteEnvironmentEntries as apiDeleteEnvironmentEntries,
	downloadEnvironmentFilesArchive,
	EnvironmentFileConflictError,
	EnvironmentFolderConflictError,
	environmentFileContentUrl,
	EnvironmentFilesInitializeError,
	extractEnvironmentArchive as apiExtractEnvironmentArchive,
	initializeEnvironmentFiles,
	listEnvironmentDirectoryChildren,
	listEnvironmentStatuses,
	lookupEnvironmentFileByPath,
	lookupEnvironmentFilesByIds,
	moveEnvironmentEntry as apiMoveEnvironmentEntry,
	renameEnvironmentEntry as apiRenameEnvironmentEntry,
	listFilteredEnvironmentFiles,
	saveEnvironmentFileContent,
	uploadEnvironmentFile,
	waitForEnvironmentImport,
	type EnvironmentEntryDto,
	type EnvironmentFileChildrenDto,
	type EnvironmentExtractResultDto,
	type EnvironmentFileDto,
} from "@/lib/api";
import { downloadBlob, fetchFile, fileFetchInit } from "@/lib/download-file";
import {
	asOsDropSource,
	extractDroppedFiles,
	type DroppedUploadBatch,
} from "@/lib/dnd/os-file-drop";
import { decodeUtf8 } from "@/lib/file-type";
import { withLoadingToast } from "@/lib/loading-toast";
import { openQuickOpen } from "@/lib/quick-open-event";
import {
	getMovableEnvironmentPaths,
	getTopLevelEnvironmentPaths,
} from "@/lib/environment-path-set";
import { selectEffectiveEnvironmentId, useChatStore } from "@/stores/useChatStore";
import { useToastStore } from "@/stores/useToastStore";

export { getTopLevelEnvironmentPaths } from "@/lib/environment-path-set";

/**
 * ファイル本体を取得して文字列で返す。テキストとして解釈できない場合は null を返す
 * (呼び出し側で未対応形式として扱う)。扱いが呼び出し側で異なるためトーストは出さない。
 * `bypassCache` は、409 直後でファイルの version がまだ更新されておらず
 * 同じ URL を再取得する場合など、バスターに頼れず no-store が必要なときだけ渡す。
 */
export async function fetchEnvironmentFileContent(
	environmentId: string,
	fileId: string,
	version: string,
	options?: { bypassCache?: boolean },
): Promise<string | null> {
	const url = environmentFileContentUrl(environmentId, fileId, { version });
	const init = fileFetchInit(url);
	const response = await fetchFile(
		url,
		options?.bypassCache ? { ...init, cache: "no-store" } : init,
	);
	if (!response.ok) throw new Error(`ファイルの取得に失敗しました (${response.status})`);
	return decodeUtf8(new Uint8Array(await response.arrayBuffer()));
}

interface UploadPathSource {
	name: string;
	webkitRelativePath?: string;
}

/**
 * 環境ごとの取得世代。リフレッシュを開始するたびに進み、進んだ後に完了した古い取得
 * (単発のディレクトリ読み込みを含む) はストアへ反映しない。
 */
const environmentLoadGenerations = new Map<string, number>();
/** 同じディレクトリ・オフセットへの取得が重なったときに 1 本へまとめるための in-flight 表。 */
const inFlightDirectoryLoads = new Map<string, Promise<void>>();

/** リフレッシュ時にディレクトリを取り直す並列数。 */
const REFRESH_CONCURRENCY = 4;

function isEnvironmentLoadCurrent(environmentId: string, generation: number): boolean {
	return (
		(environmentLoadGenerations.get(environmentId) ?? 0) === generation &&
		selectEffectiveEnvironmentId(useChatStore.getState()) === environmentId
	);
}

/**
 * 指定ディレクトリの直下一覧を取得してストアへ反映する。フォルダを開いたときや
 * 「さらに表示」から呼ぶ。`offset` を指定した場合は既存のエントリへ追記する。
 * 取得中にリフレッシュが始まったり別環境へ切り替わった場合は反映しない。
 */
export async function loadEnvironmentDirectory(
	environmentId: string,
	dirPath: string,
	offset = 0,
): Promise<void> {
	const key = `${environmentId}\n${dirPath}\n${offset}`;
	const existing = inFlightDirectoryLoads.get(key);
	if (existing) return existing;

	const generation = environmentLoadGenerations.get(environmentId) ?? 0;
	const load = (async () => {
		const children = await listEnvironmentDirectoryChildren(environmentId, dirPath, offset);
		if (!isEnvironmentLoadCurrent(environmentId, generation)) return;
		useChatStore
			.getState()
			.setEnvironmentDirectoryChildren(dirPath, children, { append: offset > 0 });
	})();
	inFlightDirectoryLoads.set(key, load);
	const settle = () => {
		if (inFlightDirectoryLoads.get(key) === load) inFlightDirectoryLoads.delete(key);
	};
	// 失敗も含めて in-flight 表から必ず外す (呼び出し側は返した load 側で失敗を受け取る)。
	void load.then(settle, settle);
	return load;
}

/** まだ読み込んでいないディレクトリだけを取得する。 */
async function ensureDirectoryLoaded(environmentId: string, dirPath: string): Promise<void> {
	if (useChatStore.getState().environmentFileChildren?.has(dirPath)) return;
	await loadEnvironmentDirectory(environmentId, dirPath);
}

/** ファイルツリーで指定エントリの位置を示すために必要な祖先ディレクトリを並列に読み込む。 */
export async function ensureDirectoryPathLoaded(
	environmentId: string,
	entryPath: string,
): Promise<void> {
	const segments = entryPath.split("/").filter(Boolean);
	const ancestorDirs = segments.map((_, index) => segments.slice(0, index).join("/"));
	await Promise.all(ancestorDirs.map((dirPath) => ensureDirectoryLoaded(environmentId, dirPath)));
}

/**
 * 1 ディレクトリを取り直す。「さらに表示」で追加読み込み済みだった分は、リフレッシュ後も
 * 同じ件数まで表示が戻るようページを続けて取得する。
 */
async function fetchDirectoryPages(
	environmentId: string,
	dirPath: string,
	minimumCount: number,
): Promise<EnvironmentFileChildrenDto> {
	let page = await listEnvironmentDirectoryChildren(environmentId, dirPath, 0);
	const entries = [...page.entries];
	while (page.hasMore && entries.length < minimumCount) {
		page = await listEnvironmentDirectoryChildren(environmentId, dirPath, entries.length);
		if (page.entries.length === 0) break;
		entries.push(...page.entries);
	}
	return { entries, hasMore: page.hasMore };
}

/**
 * 親の一覧から消えたディレクトリを、その配下のキーごと取り除く。浅い順に見ていくので、
 * 親が落ちた時点で子孫も落ちる。
 */
function pruneUnreachableDirectories(
	results: ReadonlyMap<string, EnvironmentFileChildrenDto>,
): Map<string, EnvironmentFileChildrenDto> {
	const depthOf = (dirPath: string) => (dirPath === "" ? 0 : dirPath.split("/").length);
	const ordered = [...results.keys()].sort((a, b) => depthOf(a) - depthOf(b));
	const next = new Map<string, EnvironmentFileChildrenDto>();
	const removed = new Set<string>();
	for (const dirPath of ordered) {
		if (dirPath === "") {
			next.set(dirPath, results.get(dirPath)!);
			continue;
		}
		const parent = splitPath(dirPath).dir;
		const refreshedParent = results.get(parent);
		const vanished =
			refreshedParent !== undefined &&
			!refreshedParent.entries.some((entry) => entry.isDirectory && entry.path === dirPath);
		if (removed.has(parent) || vanished) {
			removed.add(dirPath);
			continue;
		}
		next.set(dirPath, results.get(dirPath)!);
	}
	return next;
}

/** 開いているタブの id を索引と照合し、消えたファイルのタブを閉じる。 */
export async function pruneOpenEnvironmentFileTabs(
	environmentId: string,
	isCurrent: () => boolean,
): Promise<void> {
	const openFileIds = [...useChatStore.getState().openFileIds];
	const files =
		openFileIds.length === 0 ? [] : await lookupEnvironmentFilesByIds(environmentId, openFileIds);
	if (!isCurrent()) return;
	useChatStore.getState().pruneOpenEnvironmentFiles(openFileIds, files);
}

/**
 * 読み込み済みのディレクトリをまとめて取り直してストアへ反映する。ターン終了時・
 * ターミナル終了時・各ミューテーション後・チャット切替時に呼ぶ。ルートは常に対象。
 * 取得中に別環境へ切り替わっていた場合や、同一環境のより新しいリフレッシュが
 * 開始されていた場合は、古い成功・失敗を反映しない。isCurrent を指定した場合は、
 * 呼び出し側の operation が一覧へ反映可能かも応答時に再検証する。
 */
export async function refreshEnvironmentFiles(
	environmentId: string,
	options: { notifyOnError?: boolean; isCurrent?: () => boolean } = {},
): Promise<boolean> {
	const generation = (environmentLoadGenerations.get(environmentId) ?? 0) + 1;
	environmentLoadGenerations.set(environmentId, generation);
	const isCurrent = () =>
		isEnvironmentLoadCurrent(environmentId, generation) && (options.isCurrent?.() ?? true);

	const loaded = useChatStore.getState().environmentFileChildren;
	const targets = [...new Set(["", ...(loaded?.keys() ?? [])])];

	try {
		const results = new Map<string, EnvironmentFileChildrenDto>();
		let cursor = 0;
		await Promise.all(
			Array.from({ length: Math.min(REFRESH_CONCURRENCY, targets.length) }, async () => {
				while (cursor < targets.length) {
					const dirPath = targets[cursor++];
					const previousCount = loaded?.get(dirPath)?.entries.length ?? 0;
					results.set(dirPath, await fetchDirectoryPages(environmentId, dirPath, previousCount));
				}
			}),
		);
		if (!isCurrent()) return false;
		useChatStore.getState().applyEnvironmentFilesRefresh(pruneUnreachableDirectories(results));
		await pruneOpenEnvironmentFileTabs(environmentId, isCurrent);
		return true;
	} catch (err: unknown) {
		if (!isCurrent()) return false;
		console.error("refreshEnvironmentFiles failed", err);
		if (options.notifyOnError ?? true) {
			useToastStore.getState().show({
				type: "error",
				message: "ワークスペースファイルの取得に失敗しました。",
			});
		}
		return false;
	}
}

/**
 * ドラフトで選択された稼働中環境を初期化し、完了後にファイル一覧を表示する。
 * 初期化または一覧取得に失敗した場合は旧ツリーを破棄して、失敗したまま表示を続けない。
 */
export async function prepareDraftEnvironment(environmentId: string): Promise<void> {
	try {
		await initializeEnvironmentFiles(environmentId);
		const refreshed = await refreshEnvironmentFiles(environmentId, { notifyOnError: false });
		if (!refreshed) {
			throw new Error("ワークスペースファイルの取得に失敗しました。");
		}
	} catch (err: unknown) {
		const store = useChatStore.getState();
		const isCurrentDraft = store.chatId === null && store.newChatEnvironmentId === environmentId;
		if (!isCurrentDraft) throw err;
		store.clearEnvironmentFiles();

		if (err instanceof EnvironmentFilesInitializeError && err.status === 409) {
			await revalidateDraftEnvironmentStatus(environmentId);
		} else {
			useToastStore.getState().show({
				type: "error",
				message: "エージェントのファイルの読み込みに失敗しました。",
			});
		}
		throw err;
	}
}

/**
 * 409 後に対象環境の状態を再取得し、処理完了時点でも同じドラフト環境が選択されている
 * 場合だけ選択解除・状態再取得要求・エラートーストを適用する。環境切替後の遅延結果は捨てる。
 */
export async function revalidateDraftEnvironmentStatus(
	environmentId: string,
	loadStatuses: () => Promise<
		Awaited<ReturnType<typeof listEnvironmentStatuses>>
	> = listEnvironmentStatuses,
): Promise<void> {
	try {
		await loadStatuses();
	} catch (err: unknown) {
		console.error("環境状態の再取得に失敗しました", err);
	}

	const current = useChatStore.getState();
	if (current.chatId !== null || current.newChatEnvironmentId !== environmentId) return;

	current.setNewChatEnvironmentId(null);
	current.requestNewChatEnvironmentStatusRefresh();
	useToastStore.getState().show({
		type: "error",
		message: "エージェントが起動していないため、ファイルを読み込めません。",
	});
}

export function resolveEnvironmentUploadRelativePath(
	dirPath: string,
	file: UploadPathSource,
): string {
	const filePath = file.webkitRelativePath?.trim() || file.name;
	const normalizedFilePath = filePath.replaceAll("\\", "/").split("/").filter(Boolean).join("/");
	if (!normalizedFilePath) {
		throw new Error("アップロード対象のファイルパスが空です。");
	}
	return dirPath ? `${dirPath}/${normalizedFilePath}` : normalizedFilePath;
}

/** ワークスペース (コンテナのホームディレクトリ) のマウント先。backend の SandboxPath.HomeDir と一致させる。 */
const HOME_DIR = "/root";

/**
 * エージェントが渡すパス (`~/` 形式・`/root` 形式・相対・絶対のいずれも可) をコンテナ内の絶対パスへ
 * 解決する。`.` は除去し `..` は一段戻す (ルートでクランプ)。
 * backend の SandboxPath.ResolveContainerPath と挙動を揃える。
 * バックスラッシュは Linux では通常のファイル名文字なので、区切りへ読み替えず素通しする
 * (読み替えると `~\notes.pdf` を backend と別のファイルとして扱ってしまう)。
 */
function resolveContainerPath(path: string): string {
	const trimmed = path.trim();

	let absolute: string;
	if (trimmed === "~") {
		absolute = HOME_DIR;
	} else if (trimmed.startsWith("~/")) {
		absolute = `${HOME_DIR}/${trimmed.slice(2)}`;
	} else if (trimmed.startsWith("/")) {
		absolute = trimmed;
	} else {
		absolute = `${HOME_DIR}/${trimmed}`;
	}

	const segments: string[] = [];
	for (const segment of absolute.split("/")) {
		if (segment === "" || segment === ".") continue;
		if (segment === "..") {
			segments.pop();
			continue;
		}
		segments.push(segment);
	}
	return `/${segments.join("/")}`;
}

/**
 * エージェントが渡すパスを、索引キーと同じワークスペース相対パス ('/' 区切り) へ正規化する。
 * ホームディレクトリ配下を指す場合はその相対パス (ルート自身は空文字列)、ホームディレクトリ外
 * (`/tmp` など) を指す場合は null を返す。backend の SandboxPath.ToHomeDirRelative と挙動を揃える。
 */
export function toHomeDirRelative(path: string): string | null {
	const resolved = resolveContainerPath(path);

	if (resolved === HOME_DIR) return "";
	if (resolved.startsWith(`${HOME_DIR}/`)) {
		return resolved.slice(HOME_DIR.length + 1);
	}
	return null;
}

/**
 * ホームディレクトリ配下ならホーム相対、ホーム外ならコンテナ絶対パスを返す。同じファイルを指す
 * 異なる書き方 (`spec.pdf` / `./spec.pdf` / `~/spec.pdf` / `/root/spec.pdf`) が 1 つの文字列に
 * 収まるので、ファイルの同一性判定に使える。ホーム外を null にしない点が
 * {@link toHomeDirRelative} と異なる (ツールはホーム外のファイルも読めるため)。
 * backend の SandboxPath.ToHomeRelativeOrAbsolute と挙動を揃える。
 */
export function toHomeRelativeOrAbsolute(path: string): string {
	return toHomeDirRelative(path) ?? resolveContainerPath(path);
}

/**
 * エージェントが渡した形式のパスをワークスペース相対へ正規化し、該当ファイルを右ペインのタブで開く。
 * サーバーへパス解決を問い合わせて開き、ツリー上の位置は祖先ディレクトリを読み込みながら裏で示す。
 * フォルダならファイルツリーでそのフォルダを展開して選ぶ。
 * 見つからない場合はトーストで通知する。`asPreview: false` なら既存のプレビュータブを置き換えず通常のタブで開く。
 */
export async function openEnvironmentFileByPath(
	path: string,
	{ asPreview = true }: { asPreview?: boolean } = {},
): Promise<void> {
	const relativePath = toHomeDirRelative(path);
	if (!relativePath) {
		useToastStore
			.getState()
			.show({ type: "error", message: "ワークスペース内のファイルではありません。" });
		return;
	}

	const environmentId = selectEffectiveEnvironmentId(useChatStore.getState());
	if (!environmentId) return;

	let entry: EnvironmentFileDto | "directory" | null;
	try {
		entry = await lookupEnvironmentEntry(environmentId, relativePath);
	} catch (err) {
		useToastStore.getState().show({ type: "error", message: "ファイルを開けませんでした。" });
		throw err;
	}
	if (selectEffectiveEnvironmentId(useChatStore.getState()) !== environmentId) return;
	if (entry === null) {
		showFileNotFoundToast();
	} else if (entry === "directory") {
		await revealEnvironmentDirectory(environmentId, relativePath);
	} else {
		revealAndOpenEnvironmentFile(environmentId, entry, asPreview);
	}
}

/**
 * 回答の中でパスとして言及されたファイルを右ペインのプレビュータブで開く。`~/` や `/root/` の
 * 絶対パスはそのまま開く。相対パスとファイル名だけのものは、末尾が一致するファイルを大文字小文字を
 * 区別せずに探す。ホーム直下と同じパスのものか 1 件だけならそれを開き、複数あればクイックオープンで
 * 選ばせる。ファイルが無くフォルダを指していれば、ファイルツリーでそのフォルダを開く。
 */
export async function openMentionedEnvironmentFile(path: string): Promise<void> {
	if (path.startsWith("~") || path.startsWith("/")) return openEnvironmentFileByPath(path);

	const environmentId = selectEffectiveEnvironmentId(useChatStore.getState());
	if (!environmentId) return;

	const relativePath = path.replace(LEADING_RELATIVE_SEGMENTS, "");
	let files: EnvironmentFileDto[];
	let isDirectory = false;
	try {
		files = await findEnvironmentFilesByPathSuffix(environmentId, relativePath);
		if (files.length === 0) isDirectory = await isEnvironmentDirectory(environmentId, relativePath);
	} catch (err) {
		useToastStore.getState().show({ type: "error", message: "ファイルを開けませんでした。" });
		throw err;
	}
	if (selectEffectiveEnvironmentId(useChatStore.getState()) !== environmentId) return;

	const exact = files.find((file) => file.path === relativePath);
	if (exact || files.length === 1) {
		revealAndOpenEnvironmentFile(environmentId, exact ?? files[0], true);
		return;
	}
	if (files.length > 1) {
		openQuickOpen(relativePath);
		return;
	}
	if (isDirectory) {
		await revealEnvironmentDirectory(environmentId, relativePath);
		return;
	}
	showFileNotFoundToast();
}

const LEADING_RELATIVE_SEGMENTS = /^(?:\.\.?\/)+/;

function showFileNotFoundToast(): void {
	useToastStore.getState().show({ type: "error", message: "ファイルが見つかりません。" });
}

/** パスが `relativePath` と同じか、`/relativePath` で終わるファイルを大文字小文字を区別せずに全件返す。 */
async function findEnvironmentFilesByPathSuffix(
	environmentId: string,
	relativePath: string,
): Promise<EnvironmentFileDto[]> {
	const target = relativePath.toLowerCase();
	const files = await listFilteredEnvironmentFiles(environmentId, { suffixes: [relativePath] });
	return files.filter((file) => {
		const path = file.path.toLowerCase();
		return path === target || path.endsWith(`/${target}`);
	});
}

/** ワークスペース相対パスが指すファイルを返す。フォルダなら "directory"、どちらでもなければ null。 */
async function lookupEnvironmentEntry(
	environmentId: string,
	relativePath: string,
): Promise<EnvironmentFileDto | "directory" | null> {
	const file = await lookupEnvironmentFileByPath(environmentId, relativePath);
	if (file) return file;
	return (await isEnvironmentDirectory(environmentId, relativePath)) ? "directory" : null;
}

/**
 * ワークスペース相対パスがフォルダかを、親フォルダの一覧から調べる。lookup はフォルダを
 * 「見つからない」と返し、空のフォルダは直下の一覧も空になるため、どちらでも見分けられない。
 */
async function isEnvironmentDirectory(environmentId: string, path: string): Promise<boolean> {
	const parent = splitPath(path).dir;
	let offset = 0;
	for (;;) {
		const page = await listEnvironmentDirectoryChildren(environmentId, parent, offset);
		if (page.entries.some((entry) => entry.isDirectory && entry.path === path)) return true;
		// 一覧はフォルダが先に並ぶため、ファイルが出たら以降にフォルダは無い。
		if (!page.hasMore || !page.entries.at(-1)?.isDirectory) return false;
		offset += page.entries.length;
	}
}

/** ファイルツリーでフォルダを読み込み、展開して選んだ状態で見せる。 */
async function revealEnvironmentDirectory(environmentId: string, dirPath: string): Promise<void> {
	await Promise.all([
		ensureDirectoryPathLoaded(environmentId, dirPath),
		ensureDirectoryLoaded(environmentId, dirPath),
	]);
	if (selectEffectiveEnvironmentId(useChatStore.getState()) !== environmentId) return;
	useChatStore.getState().navigateToEnvironmentFilesTreeNode(dirPath);
}

/**
 * 右ペインのタブで開き、ファイルツリーでの位置は祖先ディレクトリを読み込みながら示す。
 * ツリーの読み込みは本体の取得を遅らせないよう待たない。
 */
function revealAndOpenEnvironmentFile(
	environmentId: string,
	file: EnvironmentFileDto,
	asPreview: boolean,
): void {
	const store = useChatStore.getState();
	store.mergeOpenEnvironmentFileEntries([file]);
	if (asPreview) store.openFilePreview(file.id);
	else store.openFile(file.id);
	void revealEnvironmentFileInTree(environmentId, file).catch((err: unknown) => {
		console.error("Failed to reveal the opened file in the file tree", err);
	});
}

async function revealEnvironmentFileInTree(
	environmentId: string,
	file: EnvironmentFileDto,
): Promise<void> {
	await ensureDirectoryPathLoaded(environmentId, file.path);
	const state = useChatStore.getState();
	// 読み込み中に別のファイルへ移っていたら、ツリーの選択を古いファイルへ戻さない。
	if (selectEffectiveEnvironmentId(state) !== environmentId || state.activeFileId !== file.id) {
		return;
	}
	state.navigateToEnvironmentFilesTreeNode(file.path);
}

/**
 * ワークスペース相対パスをファイル ID へ解決する。開いているタブと読み込み済みディレクトリを
 * 先に見て、無ければサーバーへ問い合わせる。ディレクトリと未存在は null。
 */
export async function resolveEnvironmentFileIdByPath(path: string): Promise<string | null> {
	const state = useChatStore.getState();
	for (const file of state.openFileEntries.values()) {
		if (!file.isDirectory && file.path === path) return file.id;
	}
	const loaded = state.environmentFileChildren?.get(splitPath(path).dir);
	const entry = loaded?.entries.find((candidate) => candidate.path === path);
	if (entry) return entry.isDirectory ? null : entry.id;

	const environmentId = selectEffectiveEnvironmentId(state);
	if (!environmentId) return null;
	const file = await lookupEnvironmentFileByPath(environmentId, path);
	// 解決中に環境が切り替わっていたら、切替後の表示に無関係な結果を持ち込まない。
	if (selectEffectiveEnvironmentId(useChatStore.getState()) !== environmentId) return null;
	if (!file || file.isDirectory) return null;
	useChatStore.getState().mergeOpenEnvironmentFileEntries([file]);
	return file.id;
}

/** アップロード対象1件。relativePath は D&D 走査で確定したワークスペース内の相対パス。 */
export interface EnvironmentUploadItem {
	file: File;
	relativePath?: string;
}

/** 一括アップロードへ追加する空フォルダと、UIへ反映してよい operation の判定を表す。 */
export interface EnvironmentUploadOptions {
	emptyDirectories?: readonly string[];
	usedFlatFallback?: boolean;
	isVisible?: () => boolean;
	/** 完了の通知を呼び出し側の表示に任せる (チャット添付は送信したメッセージが完了を示す)。 */
	suppressSuccessToast?: boolean;
	/** 共有ボリューム (デバイスは写し、クラウドは mount 先) に置く。dirPath はその根からの相対パス。 */
	sharedVolumeId?: string;
	/** 置いた後に表示を取り直す。既定は ~ のツリー。 */
	refresh?: () => Promise<unknown>;
	/** 空のフォルダ (dirPath を足したパス) を作る。既にあれば成功にする。既定は ~ に作る。 */
	createEmptyDirectory?: (relativePath: string) => Promise<void>;
}

/**
 * アップロード項目1件について、API へ渡す相対パスを解決する。File 直渡しはブラウザが付与する
 * webkitRelativePath を、EnvironmentUploadItem は走査で確定した relativePath を使う。
 */
export function resolveUploadItemPath(dirPath: string, item: File | EnvironmentUploadItem): string {
	if (item instanceof File) return resolveEnvironmentUploadRelativePath(dirPath, item);
	return resolveEnvironmentUploadRelativePath(dirPath, {
		name: item.file.name,
		webkitRelativePath: item.relativePath,
	});
}

/**
 * 複数ファイルと空フォルダをワークスペースの指定ディレクトリへアップロードする。
 * フォルダ選択時はブラウザが付与する webkitRelativePath を使い、選択フォルダ配下の構造を保つ。
 * OS からの D&D のように相対パスを自前で確定している場合は EnvironmentUploadItem で渡す。
 * 一覧更新と通知は、呼び出し側の operation が現在も表示対象である場合だけ行う。
 */
export async function uploadFilesToEnvironment(
	environmentId: string,
	dirPath: string,
	items: readonly (File | EnvironmentUploadItem)[],
	onProgress?: (percent: number) => void,
	options: EnvironmentUploadOptions = {},
): Promise<void> {
	const emptyDirectories = [...new Set(options.emptyDirectories ?? [])].map((path) =>
		resolveEnvironmentUploadRelativePath(dirPath, { name: path, webkitRelativePath: path }),
	);
	if (items.length === 0 && emptyDirectories.length === 0) return;

	const uploads: EnvironmentUploadItem[] = items.map((item) =>
		item instanceof File ? { file: item, relativePath: item.webkitRelativePath } : item,
	);
	const totalBytes = sum(uploads, (upload) => upload.file.size);
	let completedBytes = 0;
	let changedAny = false;
	const isVisible = options.isVisible ?? (() => true);
	const refresh =
		options.refresh ?? (() => refreshEnvironmentFiles(environmentId, { isCurrent: isVisible }));
	// ドロップ・ダイアログ選択のどちらの経路もここを通るため、進行中トーストはここで出す。
	await withLoadingToast(formatUploadProgressMessage(0), async (updateToast) => {
		const publishProgress = (percent: number) => {
			onProgress?.(percent);
			updateToast(formatUploadProgressMessage(percent));
		};

		const reportProgress = (currentFile: File, currentFilePercent: number) => {
			if (totalBytes === 0) {
				publishProgress(0);
				return;
			}
			const currentBytes = Math.round((currentFile.size * currentFilePercent) / 100);
			const percent = Math.round(((completedBytes + currentBytes) / totalBytes) * 100);
			publishProgress(Math.min(100, percent));
		};

		try {
			for (const relativePath of emptyDirectories) {
				if (options.createEmptyDirectory) {
					await options.createEmptyDirectory(relativePath);
					changedAny = true;
					continue;
				}
				try {
					await createEnvironmentFolder(environmentId, relativePath);
					changedAny = true;
				} catch (err: unknown) {
					// 既に同名エントリがある場合は作成不要として続行する。実体がファイルだった場合は
					// その配下へのアップロードがサーバー側で失敗するため、ここでは判別しない。
					if (!(err instanceof EnvironmentFolderConflictError)) throw err;
				}
			}

			// 取り込み (S3 → ワークスペース実ディスク) はサーバ側で非同期に走るため、
			// 後続ファイルの送信と並行して完了を待つ。
			const importWaits: Promise<void>[] = [];
			for (const upload of uploads) {
				const relativePath = resolveUploadItemPath(dirPath, upload);
				const { importId } = await uploadEnvironmentFile(
					upload.file,
					environmentId,
					relativePath,
					(percent) => reportProgress(upload.file, percent),
					options.sharedVolumeId,
				);
				importWaits.push(
					waitForEnvironmentImport(environmentId, importId).catch((err: unknown) => {
						const detail = err instanceof Error ? err.message : String(err);
						throw new Error(`${relativePath}: ${detail}`);
					}),
				);
				completedBytes += upload.file.size;
				changedAny = true;
				if (totalBytes > 0) {
					publishProgress(Math.round((completedBytes / totalBytes) * 100));
				}
			}

			// 100% 到達後は取り込み待ち (UI は「取り込み中…」表示)。
			publishProgress(100);
			await Promise.all(importWaits);
			if (isVisible()) {
				await refresh();
				if (isVisible() && !options.suppressSuccessToast) {
					useToastStore.getState().show(
						options.usedFlatFallback
							? {
									type: "info",
									message:
										"アップロードしましたが、ブラウザの制約によりフォルダ構造を保持できない可能性があります。",
								}
							: { type: "success", message: "アップロードしました。" },
					);
				}
			}
		} catch (err) {
			if (changedAny && isVisible()) {
				await refresh();
			}
			if (isVisible()) {
				// サイズ上限超過などサーバの理由が分かるときはそのまま見せる。
				const message =
					err instanceof Error && err.message ? err.message : "アップロードに失敗しました。";
				useToastStore.getState().show({ type: "error", message });
			}
			throw err;
		}
	});
}

/** OS からのドロップのアップロードの指定。 */
export interface DroppedUploadOptions {
	/** 呼び出し側の表示が今もこの環境か。アップロードの持ち主であることと合わせて、通知・取り直しの可否に使う。 */
	isCurrent: () => boolean;
	/** 読み取った後、置く前に確かめる。false なら置かない。失敗は知らせてから投げること。 */
	beforeUpload?: (batch: DroppedUploadBatch) => Promise<boolean>;
	/** uploadFilesToEnvironment へ渡す指定 (読み取った空フォルダなどより優先する)。 */
	upload?: EnvironmentUploadOptions;
}

/**
 * OS からドロップされたファイル・フォルダを、環境のアップロードの排他を取って dirPath へアップロードする。
 * DataTransfer は drop の同期フェーズでしか読めないため、drop ハンドラから await を挟まずに呼ぶこと。
 */
export function uploadDroppedFilesToEnvironment(
	environmentId: string,
	dataTransfer: DataTransfer,
	dirPath: string,
	options: DroppedUploadOptions,
): void {
	const operationId = useChatStore.getState().beginEnvironmentUpload(environmentId);
	if (operationId === null) return;
	const isVisible = () =>
		options.isCurrent() &&
		useChatStore.getState().isEnvironmentUploadOwner(environmentId, operationId);
	// 読み取りは drop の同期フェーズの内に始める (失敗は待つときに知らせる)。
	const extraction = extractDroppedFiles(asOsDropSource(dataTransfer));
	// fire-and-forget の境界。失敗は中で知らせ済みなので、ここではログだけ残して終える。
	void (async () => {
		try {
			let batch: DroppedUploadBatch;
			try {
				batch = await extraction;
			} catch (err) {
				if (isVisible()) {
					useToastStore.getState().show({
						type: "error",
						message: "ドロップしたファイルの読み取りに失敗しました。",
					});
				}
				throw err;
			}
			if (batch.files.length === 0 && batch.emptyDirectories.length === 0) return;
			if (options.beforeUpload && !(await options.beforeUpload(batch))) return;
			await uploadFilesToEnvironment(
				environmentId,
				dirPath,
				batch.files,
				(percent) =>
					useChatStore.getState().setEnvironmentUploadProgress(environmentId, operationId, percent),
				{
					emptyDirectories: batch.emptyDirectories,
					usedFlatFallback: batch.usedFlatFallback,
					isVisible,
					...options.upload,
				},
			);
		} finally {
			useChatStore.getState().endEnvironmentUpload(environmentId, operationId);
		}
	})().catch((err: unknown) => {
		console.error("ドロップしたファイルをアップロードできませんでした。", err);
	});
}

/**
 * ワークスペースの指定ディレクトリ配下に空フォルダを作成し、ツリーを更新する。
 * <paramref name="parentPath"/> はワークスペースルートからの相対ディレクトリパス
 * (ルート直下は空文字)。作成の成功・失敗はトーストで通知し、失敗時はエラーを再 throw する。
 * 同名エントリが既に存在する場合はトーストを出さず throw するので、呼び出し側で表示すること
 * ({@link throwIfEnvironmentEntryExists} 参照)。
 */
export async function createFolderInEnvironment(
	environmentId: string,
	parentPath: string,
	name: string,
): Promise<void> {
	const relativePath = parentPath ? `${parentPath}/${name}` : name;
	await throwIfEnvironmentEntryExists(environmentId, relativePath, name);
	try {
		await createEnvironmentFolder(environmentId, relativePath);
		await refreshEnvironmentFiles(environmentId);
		useToastStore.getState().show({ type: "success", message: "フォルダを作成しました。" });
	} catch (err) {
		useToastStore.getState().show({ type: "error", message: "フォルダの作成に失敗しました。" });
		throw err;
	}
}

/** `path` が `base` 自身、またはその配下 (`base` + "/" 始まり) かどうかを判定する。 */
export function isSameOrDescendantPath(path: string, base: string): boolean {
	return path === base || path.startsWith(`${base}/`);
}

/**
 * 選択したエントリに対する新規作成先を返す。フォルダは自身、ファイルは親ディレクトリになる。
 * 判定には読み込み済みの直下一覧を使う (選択できている時点で親は読み込み済み)。
 */
export function getEnvironmentEntryCreationParentPath(
	path: string | null,
	children: ReadonlyMap<string, EnvironmentFileChildrenDto> | null,
): string {
	if (path === null) return "";
	if (children?.has(path)) return path;
	const parent = splitPath(path).dir;
	const selected = children?.get(parent)?.entries.find((entry) => entry.path === path);
	return selected?.isDirectory ? path : parent;
}

/**
 * 削除対象のパス自身または配下で、未保存の変更があるファイル数を返す。未保存の変更は
 * 必ずタブとして開いているため、開きタブの解決結果だけで数え上げられる。
 */
export function countDirtyFilesUnder(
	openFiles: ReadonlyMap<string, EnvironmentFileDto>,
	dirtyFileIds: readonly string[],
	paths: readonly string[],
): number {
	return dirtyFileIds.filter((id) => {
		const file = openFiles.get(id);
		return file !== undefined && paths.some((path) => isSameOrDescendantPath(file.path, path));
	}).length;
}

/** 削除確認ダイアログに表示するメッセージを生成する。 */
export function buildEnvironmentDeleteMessage(
	entries: readonly TreeEntry[],
	dirtyFileCount: number,
): string {
	let message: string;
	if (entries.length === 1) {
		const [entry] = entries;
		message =
			entry.type === "directory"
				? `フォルダ「${entry.name}」とその中身をすべて削除しますか？ 削除した内容は取り戻せません。`
				: `「${entry.name}」を削除しますか？ 削除した内容は取り戻せません。`;
	} else {
		message = `選択した${entries.length}件を削除しますか？ 削除した内容は取り戻せません。`;
	}
	return dirtyFileCount > 0 ? `${message} 未保存の変更は失われます。` : message;
}

/**
 * 選択したファイルまたはフォルダを削除し、ツリーと開いているタブを更新する。
 * 削除中に別環境へ切り替わった場合、完了通知は切替後の環境に対する誤表示になるため出さない
 * (削除自体は元の環境に対して正しく完了する)。
 */
export async function deleteEnvironmentEntries(
	environmentId: string,
	paths: readonly string[],
): Promise<void> {
	const topLevelPaths = getTopLevelEnvironmentPaths(paths);
	if (topLevelPaths.length === 0) return;
	const isCurrentEnvironment = () =>
		selectEffectiveEnvironmentId(useChatStore.getState()) === environmentId;

	try {
		const { deletedCount } = await apiDeleteEnvironmentEntries(environmentId, topLevelPaths);
		await refreshEnvironmentFiles(environmentId);
		if (!isCurrentEnvironment()) return;
		useToastStore.getState().show({
			type: "success",
			// deletedCount は索引に存在しなかったパスを除いた実際の削除件数 (リクエストした件数以下)。
			message: deletedCount === 1 ? "削除しました。" : `${deletedCount}件を削除しました。`,
		});
	} catch (err) {
		// 失敗時も一部のパスが削除済みの可能性があるため (バックエンド側で索引は再構築済み)、
		// ツリーを再取得して実際の状態を反映してから通知する。
		await refreshEnvironmentFiles(environmentId);
		if (isCurrentEnvironment()) {
			useToastStore.getState().show({ type: "error", message: "削除に失敗しました。" });
		}
		throw err;
	}
}

/**
 * 指定パスと同名のエントリが、読み込み済みの親ディレクトリ一覧に含まれるかを返す。
 * 親が未読込の場合や「さらに表示」の続きにある場合は判定できず false になるため、
 * 最終的な衝突判定はサーバーの 409 に委ねる。
 */
export function environmentEntryExists(
	children: ReadonlyMap<string, EnvironmentFileChildrenDto> | null,
	path: string,
): boolean {
	const parent = children?.get(splitPath(path).dir);
	return parent?.entries.some((entry) => entry.path === path) ?? false;
}

/**
 * 同名エントリが既に存在する場合、案内メッセージ付きで throw する。トーストは出さない
 * (作成ダイアログがこのメッセージをインラインで表示する前提のため)。他のミューテーション関数と
 * 異なり自己通知しないので、ダイアログを介さずに呼び出す場合は呼び出し側で必ず表示すること。
 */
async function throwIfEnvironmentEntryExists(
	environmentId: string,
	relativePath: string,
	name: string,
): Promise<void> {
	await ensureDirectoryLoaded(environmentId, splitPath(relativePath).dir);
	if (environmentEntryExists(useChatStore.getState().environmentFileChildren, relativePath)) {
		throw new Error(`「${name}」は既に存在します。`);
	}
}

/**
 * ワークスペースの指定ディレクトリ配下に空ファイルを作成して開く。
 * 一覧反映後にタブを開くことで、ストアのタブ整理に新規ファイルが消されないようにする。
 * 同名エントリが既に存在する場合はトーストを出さず throw するので、呼び出し側で表示すること
 * ({@link throwIfEnvironmentEntryExists} 参照)。
 */
export async function createFileInEnvironment(
	environmentId: string,
	parentPath: string,
	name: string,
): Promise<void> {
	const relativePath = parentPath ? `${parentPath}/${name}` : name;
	await throwIfEnvironmentEntryExists(environmentId, relativePath, name);

	try {
		const created = await createEnvironmentFile(environmentId, relativePath);
		await refreshEnvironmentFiles(environmentId);
		useChatStore.getState().mergeOpenEnvironmentFileEntries([created]);
		useChatStore.getState().openFile(created.id);
		useToastStore.getState().show({ type: "success", message: "ファイルを作成しました。" });
	} catch (err) {
		useToastStore.getState().show({ type: "error", message: "ファイルの作成に失敗しました。" });
		throw err;
	}
}

/** 選択したエントリをまとめて指定ディレクトリへ移動する。 */
export async function moveEnvironmentEntries(
	environmentId: string,
	sourcePaths: readonly string[],
	destinationDirPath: string,
): Promise<void> {
	const paths = getMovableEnvironmentPaths(sourcePaths, destinationDirPath);
	if (paths.length === 0) {
		useToastStore.getState().show({ type: "info", message: "移動対象がありませんでした。" });
		return;
	}

	const destinationPaths = paths.map((path) => {
		const name = path.split("/").pop()!;
		return destinationDirPath ? `${destinationDirPath}/${name}` : name;
	});
	const duplicateDestination = destinationPaths.find(
		(destination, index) => destinationPaths.indexOf(destination) !== index,
	);
	if (duplicateDestination) {
		useToastStore.getState().show({
			type: "error",
			message: `移動先で「${duplicateDestination.split("/").pop()}」が重複します。`,
		});
		return;
	}

	let movedCount = 0;
	try {
		// 移動先の直下一覧で衝突を先に弾く。未読込なら読み込んでから判定する。
		await ensureDirectoryLoaded(environmentId, destinationDirPath);
		const children = useChatStore.getState().environmentFileChildren;
		const collision = destinationPaths.find((destination) =>
			environmentEntryExists(children, destination),
		);
		if (collision) {
			useToastStore.getState().show({
				type: "error",
				message: `移動先に「${collision.split("/").pop()}」が既に存在します。`,
			});
			return;
		}

		for (; movedCount < paths.length; movedCount += 1) {
			await apiMoveEnvironmentEntry(environmentId, paths[movedCount], destinationPaths[movedCount]);
		}
		await refreshEnvironmentFiles(environmentId);
		useToastStore.getState().show({
			type: "success",
			message: paths.length === 1 ? "移動しました。" : `${paths.length}件を移動しました。`,
		});
	} catch (err) {
		await refreshEnvironmentFiles(environmentId);
		useToastStore.getState().show({
			type: "error",
			message:
				movedCount === 0
					? "移動に失敗しました。"
					: `${movedCount}/${paths.length}件を移動した時点で失敗しました。`,
		});
		throw err;
	}
}

/** アーカイブを同じディレクトリへ展開して一覧を更新し、展開先を返す。失敗時はトーストを出して再送出する。 */
export async function extractEnvironmentArchive(
	environmentId: string,
	archivePath: string,
): Promise<EnvironmentExtractResultDto> {
	try {
		// 大きなアーカイブは展開に時間がかかるため、完了まで進行中トーストを出しておく。
		const result = await withLoadingToast(
			`「${splitPath(archivePath).name}」を展開中…`,
			async () => {
				const extracted = await apiExtractEnvironmentArchive(environmentId, archivePath);
				await refreshEnvironmentFiles(environmentId);
				return extracted;
			},
		);
		useToastStore.getState().show({
			type: "success",
			message: `「${splitPath(result.destinationPath).name}」へ展開しました。`,
		});
		return result;
	} catch (err) {
		useToastStore.getState().show({
			type: "error",
			message: err instanceof Error ? err.message : "展開に失敗しました。",
		});
		throw err;
	}
}

/** 単一エントリに畳まれた場合はその名前を、複数の場合はタイムスタンプ名を ZIP ファイル名にする。 */
function buildArchiveFileName(topLevelPaths: readonly string[]): string {
	if (topLevelPaths.length === 1) {
		const path = topLevelPaths[0];
		return `${path.slice(path.lastIndexOf("/") + 1)}.zip`;
	}
	const now = new Date();
	const timestamp =
		[
			now.getFullYear().toString().padStart(4, "0"),
			(now.getMonth() + 1).toString().padStart(2, "0"),
			now.getDate().toString().padStart(2, "0"),
		].join("") +
		"-" +
		[now.getHours(), now.getMinutes(), now.getSeconds()]
			.map((value) => value.toString().padStart(2, "0"))
			.join("");
	return `environment-files-${timestamp}.zip`;
}

/** 選択した環境ファイルをZIPでダウンロードする。 */
export async function downloadEnvironmentEntriesAsZip(
	environmentId: string,
	paths: readonly string[],
): Promise<void> {
	const topLevelPaths = getTopLevelEnvironmentPaths(paths);
	if (topLevelPaths.length === 0) return;
	try {
		const archive = await withLoadingToast("ZIPファイルを作成中…", () =>
			downloadEnvironmentFilesArchive(environmentId, topLevelPaths),
		);
		downloadBlob(archive, buildArchiveFileName(topLevelPaths));
		useToastStore.getState().show({
			type: "success",
			message: "ZIPファイルをダウンロードしました。",
		});
	} catch (err) {
		useToastStore.getState().show({
			type: "error",
			message: "ZIPファイルのダウンロードに失敗しました。",
		});
		throw err;
	}
}

/**
 * ツリー内のエントリ (ファイル / フォルダ) を同一ディレクトリ内で名前変更し、ツリーを更新する。
 * <paramref name="newName"/> はパス区切りを含まない新しい名前。同一ディレクトリに同名のエントリが
 * 既にある場合はサーバへ要求を送らず、案内のトーストを表示して終了する。名前が変わらない場合は
 * 何もしない。成功・失敗をトーストで通知し、失敗時はエラーを再 throw する。
 */
export async function renameEnvironmentEntry(
	environmentId: string,
	sourcePath: string,
	newName: string,
): Promise<void> {
	const slash = sourcePath.lastIndexOf("/");
	const parentPath = slash >= 0 ? sourcePath.slice(0, slash) : "";
	const currentName = sourcePath.slice(slash + 1);
	if (newName === currentName) return;

	const destinationPath = parentPath ? `${parentPath}/${newName}` : newName;

	try {
		// 同名衝突は読み込み済みの直下一覧でサーバ往復前に検出する (未読込なら読み込む)。
		await ensureDirectoryLoaded(environmentId, parentPath);
		if (environmentEntryExists(useChatStore.getState().environmentFileChildren, destinationPath)) {
			useToastStore.getState().show({
				type: "error",
				message: `「${newName}」は既に存在します。`,
			});
			return;
		}

		await apiRenameEnvironmentEntry(environmentId, sourcePath, newName);
		await refreshEnvironmentFiles(environmentId);
		useToastStore.getState().show({ type: "success", message: "名前を変更しました。" });
	} catch (err) {
		useToastStore.getState().show({ type: "error", message: "名前の変更に失敗しました。" });
		throw err;
	}
}

/** 保存時の楽観ロックと通知方法を指定する。 */
export interface SaveEnvironmentFileOptions {
	force?: boolean;
	/** 成功・失敗トーストを抑制する (自動保存用。失敗は呼び出し側が通知する)。 */
	notify?: "full" | "silent";
}

/**
 * 開いているファイルの内容を保存し、成功時はワークスペース一覧を更新する。
 * サーバー側の変更と衝突した場合は `EnvironmentFileConflictError` を投げる (呼び出し側が
 * 上書き確認ダイアログを出して `force: true` で再試行するかを判断する)。それ以外の失敗は
 * `options.notify` が `"full"` (既定) のときだけエラートーストを表示したうえで rethrow する
 * (`"silent"` のときは呼び出し側がフィードバックを担う)。
 */
export async function saveEnvironmentFile(
	environmentId: string,
	fileId: string,
	content: string,
	baseVersion: number,
	{ force = false, notify = "full" }: SaveEnvironmentFileOptions = {},
): Promise<EnvironmentFileDto> {
	try {
		const updated = await saveEnvironmentFileContent(
			environmentId,
			fileId,
			content,
			baseVersion,
			force,
		);
		await refreshEnvironmentFiles(environmentId);
		if (notify === "full") {
			useToastStore.getState().show({ type: "success", message: "保存しました。" });
		}
		return updated;
	} catch (err) {
		if (err instanceof EnvironmentFileConflictError) {
			throw err;
		}
		if (notify === "full") {
			useToastStore.getState().show({ type: "error", message: "保存に失敗しました。" });
		}
		throw err;
	}
}

/** パスを親ディレクトリ ("" = ルート直下) と basename に分割する。 */
export function splitPath(path: string): { dir: string; name: string } {
	const slash = path.lastIndexOf("/");
	return { dir: slash === -1 ? "" : path.slice(0, slash), name: path.slice(slash + 1) };
}

const HIDDEN_ROOT_FILES = new Set([".session_cwd", ".session_env"]);

/** バッシュセッション状態を保持する内部ファイルか (ルート直下のみ隠す)。 */
export function isHiddenEnvironmentFile(path: string): boolean {
	const parts = path.split("/").filter(Boolean);
	return parts.length === 1 && HIDDEN_ROOT_FILES.has(parts[0]);
}

/**
 * 読み込み済みディレクトリの直下一覧から FileEntry ツリーを構築する。未読込のディレクトリは
 * `children: null` になり、展開時に初めて取得される。並び順はサーバーが返した順のまま。
 * ルートが未読込のときは null (スケルトン表示) を返す。
 */
export function buildEnvironmentFilesTree(
	children: ReadonlyMap<string, EnvironmentFileChildrenDto>,
): TreeEntry[] | null {
	const toEntries = (dirPath: string): TreeEntry[] | null => {
		const loaded = children.get(dirPath);
		if (!loaded) return null;
		return loaded.entries
			.filter((entry) => !isHiddenEnvironmentFile(entry.path))
			.map((entry): TreeEntry => {
				const name = splitPath(entry.path).name;
				if (entry.isDirectory || entry.id === null) {
					return {
						type: "directory",
						name,
						path: entry.path,
						children: toEntries(entry.path),
						hasMore: children.get(entry.path)?.hasMore ?? false,
					} satisfies DirectoryTreeEntry;
				}
				return {
					type: "file",
					name,
					path: entry.path,
					fileId: entry.id,
					size: entry.sizeBytes,
				} satisfies FileTreeEntry;
			});
	};
	return toEntries("");
}

/**
 * 開いているタブの id → ファイル DTO を解決する。ツリーから開いたファイルは読み込み済みの
 * 直下一覧に、Quick Open や URL 復元で開いたファイルは解決結果 (`openFileEntries`) に載る。
 */
export function collectOpenEnvironmentFiles(
	environmentId: string | null,
	children: ReadonlyMap<string, EnvironmentFileChildrenDto> | null,
	openFileEntries: ReadonlyMap<string, EnvironmentFileDto>,
	openFileIds: readonly string[],
): Map<string, EnvironmentFileDto> {
	const wanted = new Set(openFileIds);
	const resolved = new Map<string, EnvironmentFileDto>();
	if (environmentId !== null) {
		for (const loaded of children?.values() ?? []) {
			for (const entry of loaded.entries) {
				if (entry.id === null || !wanted.has(entry.id)) continue;
				const file = toEnvironmentFileDto(environmentId, entry);
				if (file) resolved.set(file.id, file);
			}
		}
	}
	// 解決結果はサーバーへ問い合わせた版を優先する (ツリーより新しいとは限らないが、
	// ツリーに載らないファイルを開いている場合はこちらにしか無い)。
	for (const id of wanted) {
		const file = openFileEntries.get(id);
		if (file) resolved.set(id, file);
	}
	return resolved;
}

/** 直下一覧のエントリをファイル DTO へ変換する。ディレクトリは null。 */
function toEnvironmentFileDto(
	environmentId: string,
	entry: EnvironmentEntryDto,
): EnvironmentFileDto | null {
	if (entry.isDirectory || entry.id === null) return null;
	return {
		id: entry.id,
		environmentId,
		path: entry.path,
		isDirectory: false,
		contentType: entry.contentType ?? "",
		sizeBytes: entry.sizeBytes,
		updatedAt: entry.updatedAt ?? "",
		version: entry.version ?? 0,
	};
}

/** 選んだファイルのアップロードの指定。 */
export interface ActiveEnvironmentUploadOptions {
	/** 排他を取った後、置く前に確かめる。false なら置かない。失敗は知らせてから投げること。 */
	beforeUpload?: (environmentId: string, isVisible: () => boolean) => Promise<boolean>;
	/** uploadFilesToEnvironment へ渡す指定。 */
	upload?: EnvironmentUploadOptions;
}

/**
 * 現在有効な環境のワークスペース (sharedVolumeId を渡せば共有ボリューム) へファイル群をアップロードする。開始・進捗・終了の
 * オペレーション管理までを含む。
 * 環境が未確定、または別のアップロードが進行中の場合は何もしない (呼び出し側は
 * その状態では操作を塞いでおり、ここに来るのは同時操作が競った場合だけ)。
 */
export async function uploadFilesToActiveEnvironment(
	dirPath: string,
	files: readonly File[],
	options: ActiveEnvironmentUploadOptions = {},
): Promise<void> {
	if (files.length === 0) return;
	const store = useChatStore.getState();
	const environmentId = selectEffectiveEnvironmentId(store);
	if (environmentId === null) return;
	const operationId = store.beginEnvironmentUpload(environmentId);
	if (operationId === null) return;
	const isVisible = () => {
		const current = useChatStore.getState();
		return (
			selectEffectiveEnvironmentId(current) === environmentId &&
			current.isEnvironmentUploadOwner(environmentId, operationId)
		);
	};
	try {
		if (options.beforeUpload && !(await options.beforeUpload(environmentId, isVisible))) return;
		await uploadFilesToEnvironment(
			environmentId,
			dirPath,
			files,
			(percent) =>
				useChatStore.getState().setEnvironmentUploadProgress(environmentId, operationId, percent),
			{ isVisible, ...options.upload },
		);
	} finally {
		useChatStore.getState().endEnvironmentUpload(environmentId, operationId);
	}
}

/** チャットへ添付したファイルの置き場所 (ホーム相対)。 */
export const CHAT_ATTACHMENT_DIR = "uploads";

/** 既存名と重ならないファイル名を返す。重なる場合は拡張子の前に ` (1)`, ` (2)` … を付ける。 */
export function pickUniqueName(name: string, taken: ReadonlySet<string>): string {
	if (!taken.has(name)) return name;
	const dot = name.lastIndexOf(".");
	const [stem, ext] = dot > 0 ? [name.slice(0, dot), name.slice(dot)] : [name, ""];
	for (let i = 1; ; i++) {
		const candidate = `${stem} (${i})${ext}`;
		if (!taken.has(candidate)) return candidate;
	}
}

async function listChildNames(environmentId: string, dirPath: string): Promise<Set<string>> {
	const names = new Set<string>();
	for (let offset = 0; ; ) {
		const page = await listEnvironmentDirectoryChildren(environmentId, dirPath, offset);
		for (const entry of page.entries) names.add(entry.path.slice(entry.path.lastIndexOf("/") + 1));
		if (!page.hasMore || page.entries.length === 0) return names;
		offset += page.entries.length;
	}
}

function failChatAttachment(message: string): never {
	useToastStore.getState().show({ type: "error", message });
	throw new Error(message);
}

/** チャット添付を `~/uploads/` のどこへ置くか。path は既存ファイルと重ならないよう採番したホーム相対パス。 */
export interface ChatAttachmentUploadPlan {
	environmentId: string;
	uploads: { file: File; path: string }[];
}

/**
 * チャット添付の置き場所を、`~/uploads/` の既存ファイルと重ならないよう決める。置くファイルがなければ null。
 * 決められなければトーストで知らせたうえで throw する。
 */
export async function planChatAttachmentUploads(
	environmentId: string | null,
	files: readonly File[],
): Promise<ChatAttachmentUploadPlan | null> {
	if (files.length === 0) return null;
	if (environmentId === null) {
		return failChatAttachment("環境のないチャットには、このファイルを添付できません。");
	}
	let taken: Set<string>;
	try {
		taken = await listChildNames(environmentId, CHAT_ATTACHMENT_DIR);
	} catch (err) {
		useToastStore.getState().show({
			type: "error",
			message: err instanceof Error ? err.message : "アップロード先を確認できませんでした。",
		});
		throw err;
	}
	const uploads = files.map((file) => {
		const name = pickUniqueName(file.name, taken);
		taken.add(name);
		return { file, path: `${CHAT_ATTACHMENT_DIR}/${name}` };
	});
	return { environmentId, uploads };
}

/** {@link planChatAttachmentUploads} で決めたパスへアップロードする。失敗はトーストで知らせたうえで throw する。 */
export async function uploadChatAttachments({
	environmentId,
	uploads,
}: ChatAttachmentUploadPlan): Promise<void> {
	const operationId = useChatStore.getState().beginEnvironmentUpload(environmentId);
	if (operationId === null) {
		return failChatAttachment("別のアップロードが進行中です。完了してから送信してください。");
	}
	try {
		await uploadFilesToEnvironment(
			environmentId,
			"",
			uploads.map(({ file, path }) => ({ file, relativePath: path })),
			(percent) =>
				useChatStore.getState().setEnvironmentUploadProgress(environmentId, operationId, percent),
			{
				isVisible: () =>
					useChatStore.getState().isEnvironmentUploadOwner(environmentId, operationId),
				suppressSuccessToast: true,
			},
		);
	} finally {
		useChatStore.getState().endEnvironmentUpload(environmentId, operationId);
	}
}

/** 100% 到達後はワークスペースへの取り込み (サーバ側処理) を待っている。 */
export function formatUploadProgressMessage(percent: number): string {
	return percent >= 100 ? "取り込み中…" : `アップロード中 ${percent}%`;
}
